import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { QuoteButton } from "@/components/QuoteButton";
import { useReveal, Seo } from "@/lib/seo";
import { api, mediaUrl } from "@/lib/api";
import { AMBIANCE_KEYS, GALLERY_CATEGORIES } from "@/lib/data";
import { useSiteImage } from "@/context/ContentContext";

const FILTERS = [{ value: "all", label: "Tout" }, ...GALLERY_CATEGORIES];

export default function Realisations() {
  useReveal();
  const img = useSiteImage();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = items.filter((i) => filter === "all" || i.category === filter);

  // Ambiance fallback when no managed media yet (clearly labelled as visuel d'ambiance).
  const showAmbiance = items.length === 0;

  return (
    <div data-testid="realisations-page">
      <Seo
        title="Nos réalisations — Portfolio Afro Regal"
        description="Découvrez une sélection de réalisations Afro Regal : mariages, événements privés, entreprises, inaugurations, catering et productions."
      />
      <section className="bg-[#2C2C2C] pb-14 pt-32 lg:pb-20 lg:pt-40" data-testid="realisations-hero">
        <div className="mx-auto flex max-w-7xl flex-col gap-10 px-5 lg:flex-row lg:items-end lg:justify-between lg:px-8">
          <div className="fade-up max-w-3xl">
            <p className="mb-5 text-sm uppercase tracking-[0.3em] text-[#C9A84C]">Nos réalisations</p>
            <h1 className="font-serif text-5xl font-500 leading-none text-white sm:text-6xl lg:text-8xl">Des moments créés avec soin</h1>
            <p className="mt-6 max-w-xl text-base text-white/65">Une sélection de nos prestations, par catégorie. Photographies et vidéos de nos événements.</p>
          </div>
          <div className="hidden h-40 w-40 shrink-0 overflow-hidden rounded-full border border-[#C9A84C]/50 p-1.5 lg:block">
            <img src={img("realisations_hero")} alt="" aria-hidden="true" className="h-full w-full rounded-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {!showAmbiance && <div className="reveal sticky top-24 z-20 -mx-2 mb-10 flex flex-wrap gap-3 rounded-full bg-[#FAF6EF]/90 px-2 py-2 backdrop-blur-md" data-testid="gallery-filters">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                data-testid={`filter-${f.value}`}
                onClick={() => setFilter(f.value)}
                className={`rounded-full border px-5 py-2 text-sm transition-colors duration-200 ${
                  filter === f.value
                    ? "border-[#2C2C2C] bg-[#2C2C2C] text-white"
                    : "border-[#2C2C2C]/20 text-[#2C2C2C] hover:border-[#C9A84C]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>}

          {showAmbiance ? (
            <>
              <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
                {AMBIANCE_KEYS.concat(AMBIANCE_KEYS).map(img).map((src, i) => (
                  <div key={i} className="reveal overflow-hidden rounded-xl">
                    <img src={src} alt="Visuel d'ambiance" className="w-full object-cover" />
                  </div>
                ))}
              </div>
            </>
          ) : filtered.length === 0 ? (
            <p className="py-20 text-center text-[#2C2C2C]/60">Aucune réalisation dans cette catégorie pour le moment.</p>
          ) : (
            <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 [&>*]:mb-4">
              {filtered.map((it) => (
                <button
                  key={it.id}
                  data-testid={`gallery-item-${it.id}`}
                  onClick={() => setLightbox(it)}
                  className="reveal group relative block w-full overflow-hidden rounded-xl"
                >
                  {it.media_type === "video" ? (
                    <div className="relative">
                      <video src={mediaUrl(it.url)} className="w-full" muted playsInline />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <Play size={40} className="text-white" />
                      </div>
                    </div>
                  ) : (
                    <img src={mediaUrl(it.url)} alt={it.alt || it.caption || "Réalisation Afro Regal"} className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  )}
                  {it.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-left opacity-0 transition-opacity group-hover:opacity-100">
                      <p className="text-sm text-white">{it.caption}</p>
                      {!it.is_real && <span className="text-[10px] uppercase tracking-wide text-[#C9A84C]">Visuel d'ambiance</span>}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="reveal mt-16 flex justify-center">
            <QuoteButton testId="realisations-cta-quote" />
          </div>
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <div className="max-h-[90vh] max-w-4xl" onClick={(e) => e.stopPropagation()}>
            {lightbox.media_type === "video" ? (
              <video src={mediaUrl(lightbox.url)} controls autoPlay className="max-h-[85vh] w-full rounded-lg" />
            ) : (
              <img src={mediaUrl(lightbox.url)} alt={lightbox.alt || ""} className="max-h-[85vh] w-full rounded-lg object-contain" />
            )}
            {lightbox.caption && <p className="mt-3 text-center text-sm text-white">{lightbox.caption}</p>}
          </div>
        </div>
      )}
    </div>
  );
}
